import './App.scss'
import 'bootstrap-icons/font/bootstrap-icons.min.css'

import React from 'react'
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useRouteError } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AuthorizedRoute } from './components/AuthorizedRoute'
import { Permission } from './contexts/permission/PermissionModel'
import { LayoutWithLeftSidebar } from './views/LayoutWithLeftSidebar/LayoutWithLeftSidebar'
import { Upcoming } from './views/Upcoming/Upcoming'
import { Setup } from './views/Setup/Setup'
import { ProfileSettings } from './views/ProfileSettings/ProfileSettings'
import { ManageTeam } from './views/ManageTeam/ManageTeam'
import { Plan } from './views/Plan/Plan'
import { GettingStarted } from './views/GettingStarted/GettingStarted'
import { APIClientProvider, ClientProviderConfiguration } from './apps/professional-app/fa/APIClientProvider'
import { useAppSelector } from './store/hooks'
import { selectAuthenticatedUser, selectCrossAccountLoginLoading } from './store/auth/authSlice'
import { PastMeetings } from './apps/professional-app/fa/PastMeetings/PastMeetings'
import { DemoMeeting } from './apps/professional-app/fa/PastMeetings/DemoMeeting'
import { useGettingStarted } from './views/GettingStarted/hooks/useGettingStarted'
import IntegrationCallbackView from './views/Integration/IntegrationCallbackView'
import { LockScreenWrapper } from './views/Plan/LockScreenWrapper/LockScreenWrapper'
import { ManageContacts } from './views/Contacts/ManageContacts'
import { MeetingTemplateView } from './views/Setup/MeetingTemplates/MeetingTemplateView'
import { FinancialAdvisorSummary } from './apps/professional-app/fa/FinancialAdvisorSummary'
import { ImportSessionModal } from './apps/professional-app/fa/ImportSession'
import { Logout } from './views/Logout/Logout'
import { useUTMHandler } from './common/utm'
import { MFALogin } from './views/MFA/MFALogin'
import { Contact } from './views/Contacts/Contact'
import { EditAIFormView } from './views/Setup/AIForms/EditAIFormView'
import { Analytics } from './views/Analytics/Analytics'
import { UserView } from './views/ManageTeam/User'
import { EmailTypeView } from './views/Setup/EmailTypes/EmailTypeView'
import { EmailHubV2 } from './views/EmailV2/EmailHubV2'
import { CleanupDatabase } from './apps/in-person-meeting/views/CleanupDatabase'
import { LiveMeetings } from './views/LiveMeetings/LiveMeetings'
import { CreateInPersonMeetingLoader } from './apps/professional-app/fa/MeetingPreparation/CreateInPersonMeetingLoader'
import { MobileSupport } from './views/InPersonMeeting/MobileSupport'
import { LoadingOverlay } from './components/CrossAccount/LoadingOverlay'
import { DeltaTrial } from './views/Plan/DeltaTrial/DeltaTrial'
import { DeltaTrialSuccess } from './views/Plan/DeltaTrial/DeltaTrialSuccess'
import { NewEmailPage } from './views/EmailV2/NewEmailPage'
import { DocumentSummaryPage } from './views/ProcessDocument/DocumentSummaryPage'
import { DocumentTypeView } from './views/Setup/DocumentTypes/DocumentTypeView'
import { AttachmentSummaryPage } from './views/ProcessDocument/AttachmentSummary/AttachmentSummaryPage'
import { DocumentProspectingPage } from './views/ProcessDocument/DocumentProspectingPage'
import { OAuthAuthorize } from './views/OAuth/Authorize'
import { ROUTER_BASEPATH } from './common/constants'
import { IntegrationSettings } from './views/Integration/IntegrationSettings'
import { IntegrationManagement } from './views/Integration/IntegrationManagement/IntegrationManagement'
import { ProfileSettingsIntegrationRedirect } from './views/ProfileSettings/Integrations/ProfileSettingsIntegrationRedirect'
import { CrossClientLanding } from './views/CrossClient/Landing/CrossClientLanding'
import { CrossClientResults } from './views/CrossClient/Results/CrossClientResults'
import { FormTemplateUpdateView } from './views/Setup/FormTemplates/FormTemplateUpdateView'
import { FormTemplateCreateView } from './views/Setup/FormTemplates/FormTemplateCreateView'

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const clientProviderConfiguration: ClientProviderConfiguration = {
  mediaApiBaseUrl: import.meta.env.REACT_APP_API_BASE_URL!,
  aiApiBaseUrl: import.meta.env.REACT_APP_AI_BASE_URL!,
  aiExecuteApiBaseUrl: import.meta.env.REACT_APP_AI_EXECUTE_API_BASE_URL!,
  trackingApiBaseUrl: import.meta.env.REACT_APP_TRACKING_API_BASE_URL!,
  trackingEnabled: import.meta.env.REACT_APP_TRACKING_ENABLED === 'true',
}

const AppComponent: React.FC = () => {
  const isLoading = useAppSelector(selectCrossAccountLoginLoading)
  return (
    <>
      <LoadingOverlay show={isLoading} message='Switching accounts…' />
      <AppRoutes />
    </>
  )
}

export const App = Sentry.withProfiler(AppComponent)

const RootErrorElement: React.FC = () => {
  const error = useRouteError()

  React.useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className='d-flex flex-column align-items-center justify-content-center vh-100'>
      <h1 className='mb-3'>Something went wrong</h1>
      <p className='text-center'>An unexpected error has occurred. Please try refreshing the page or contact support if the issue persists.</p>
    </div>
  )
}

const AppRoutes = () => {
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        path='/'
        element={
          authenticatedUser ? (
            <APIClientProvider config={clientProviderConfiguration} accountId={authenticatedUser.accountId}>
              <Outlet />
            </APIClientProvider>
          ) : null
        }
        errorElement={<RootErrorElement />}
      >
        {/*TODO: UPDATE TO CORRECT PERMISSION */}
        <Route path='/oauth/authorize' element={<AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true} />}>
          <Route index={true} element={<OAuthAuthorize />} />
        </Route>
        <Route path='/mfa' element={<AuthorizedRoute allowedTo={[Permission.MFA_READ]} checkAuthenticationOnly={true} />}>
          <Route index={true} element={<MFALogin />} />
        </Route>
        <Route
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LayoutWithLeftSidebar />
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<HomePageNavigate />} />
        </Route>
        <Route
          path='/getting-started/'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<GettingStarted />} />
          <Route path='start' element={<GettingStarted />} />
        </Route>

        <Route
          path='/workspace'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index={true} element={<LiveMeetings />} />
          <Route path='upcoming/'>
            <Route index={true} element={<Upcoming />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
          </Route>
          <Route path='live-meetings/'>
            <Route index={true} element={<LiveMeetings />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
          </Route>
          <Route path='in-person-meeting/'>
            <Route path='cleanup-database' element={<CleanupDatabase />} />
            <Route path='mobile-support' element={<MobileSupport />} />
            <Route path='upcoming' element={<CreateInPersonMeetingLoader />} />
            <Route path='live-meetings' element={<CreateInPersonMeetingLoader />} />
          </Route>
          <Route path='setup/'>
            <Route path=':activeTab/'>
              <Route path='policies/:policyId/:modal' element={<Setup />} />
              <Route path='meeting-type/:operation/:meetingTemplateId/:tabType' element={<MeetingTemplateView />} />
              <Route path='meeting-type/:operation/:meetingTemplateId/:tabType/:modalType' element={<MeetingTemplateView />} />
              <Route path='meeting-type/create/:tabType' element={<MeetingTemplateView />} />
              <Route path='email-type/:operation/:emailTypeId' element={<EmailTypeView />} />
              <Route path='email-type/create/' element={<EmailTypeView />} />
              <Route path='document-type/:operation/:documentTypeId' element={<DocumentTypeView />} />
              <Route path='document-type/create/' element={<DocumentTypeView />} />
              <Route path='form/create' element={<EditAIFormView />} />
              <Route path='form/details/:formId'>
                <Route path=':activeFormTab?' element={<EditAIFormView />} />
                <Route path='templates/create' element={<FormTemplateCreateView />} />
                <Route path='templates/details/:formTemplateId' element={<FormTemplateUpdateView />} />
              </Route>
              <Route path='' element={<Setup />} />
            </Route>
            <Route path='' element={<Setup />} />
          </Route>
          <Route path='past-meetings/'>
            <Route index={true} element={<PastMeetings />} />
            <Route path='room/:roomId/session/:sessionId/' element={<FinancialAdvisorSummary />} />
            <Route path='import' element={<ImportSessionModal />} />
            <Route path='sample-meeting' element={<DemoMeeting />} />
          </Route>
        </Route>
        <Route
          path='/account'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='manage-team/'>
            <Route path='' element={<ManageTeam />} />
            <Route path=':activeTab' index={true} element={<ManageTeam />} />
            <Route path='users/:userId' element={<UserView />} />
            <Route
              path='users/:userId/tabs/:activeTab'
              element={
                <AuthorizedRoute allowedTo={[Permission.USER_ADMIN]}>
                  <UserView />
                </AuthorizedRoute>
              }
            />
            <Route path='users/:userId/:modal' element={<ManageTeam />} />
            <Route path='grants/:grantId/:modal' element={<ManageTeam />} />
          </Route>

          <Route path='profile-settings/' element={<ProfileSettings />}>
            <Route path=':activeTab' element={<ProfileSettings />} />
          </Route>
          <Route path='profile-settings/integrations' element={<ProfileSettingsIntegrationRedirect />} />
          <Route path='integration-settings/' element={<IntegrationSettings />} />
          <Route path='integration-settings/:serviceId' element={<IntegrationManagement />}>
            <Route path=':activeTab' element={<IntegrationManagement />} />
          </Route>
        </Route>

        <Route
          path='/account/plan'
          element={
            <AuthorizedRoute allowedTo={[Permission.BILLING_SUBSCRIPTION_CHANGE]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index element={<Plan />} />
          <Route path='free-trial/:feature' element={<DeltaTrial />} />
          <Route path='free-trial/:feature/success' element={<DeltaTrialSuccess />} />
          <Route path=':activeTab' element={<Plan />} />
        </Route>

        <Route
          path='/library'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='manage-contacts/'>
            <Route path='contacts/:userId' element={<Contact />} />
            <Route path='contacts/:userId/tabs/:activeTab/threads/:threadId' element={<Contact />}>
              <Route path='' element={<Contact />} />
              <Route path='modal/:modal/connectors/:serviceId' element={<Contact />} />
              <Route path='modal/:modal/contacts/:contactId' element={<Contact />} />
              <Route path='modal/:modal/contacts/:contactId/connectors/:serviceId' element={<Contact />} />
            </Route>
            <Route path='contacts/:userId/tabs/:activeTab/:scorecardTab?' element={<Contact />} />
            <Route path='contacts/:userId/documents'>
              <Route path='process'>
                <Route path='' element={<DocumentSummaryPage />} />
              </Route>
            </Route>
            <Route path='contacts/documents'>
              <Route path='process'>
                <Route path='' element={<DocumentProspectingPage />} />
              </Route>
            </Route>
            <Route path='contacts/:userId/emails/:emailId/attachments/:attachmentId/summary' element={<AttachmentSummaryPage />} />
            <Route path='' element={<ManageContacts />}>
              <Route path='contacts/:userId/:modal' element={<ManageContacts />} />
            </Route>
          </Route>
        </Route>

        <Route
          path='/cross-client'
          element={
            <AuthorizedRoute allowedTo={[Permission.CROSS_CLIENT_QUERY]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index element={<CrossClientLanding />} />
          <Route path='executions/:executionId' element={<CrossClientResults />} />
        </Route>

        <Route
          path='/analytics-hub'
          element={
            <AuthorizedRoute allowedTo={[Permission.DASHBOARD_LIST]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route index element={<Analytics />} />
        </Route>

        <Route
          path='/email'
          element={
            <AuthorizedRoute allowedTo={[Permission.INTEGRATION_MANAGE_EMAIL]}>
              <LockScreenWrapper>
                <LayoutWithLeftSidebar />
              </LockScreenWrapper>
            </AuthorizedRoute>
          }
        >
          <Route path='inbox/'>
            <Route path='threads/:threadId' element={<EmailHubV2 />}>
              <Route path='' element={<EmailHubV2 />} />
              <Route path='modal/:modal/connectors/:serviceId' element={<EmailHubV2 />} />
              <Route path='modal/:modal/contacts/:contactId' element={<EmailHubV2 />} />
              <Route path='modal/:modal/contacts/:contactId/connectors/:serviceId' element={<EmailHubV2 />} />
            </Route>
            <Route path='' element={<EmailHubV2 />} />
          </Route>
          <Route path='new'>
            <Route path='' element={<NewEmailPage />} />
          </Route>
        </Route>
        <Route
          path='/integrations'
          element={
            <div>
              <Outlet />
            </div>
          }
        >
          <Route
            path={'callback/:integrationType'}
            element={
              <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
                <IntegrationCallbackView />
              </AuthorizedRoute>
            }
          />
        </Route>
        <Route
          path='/logout'
          element={
            <AuthorizedRoute allowedTo={[Permission.ROOM_LIST]} checkAuthenticationOnly={true}>
              <LayoutWithLeftSidebar>
                <Logout />
              </LayoutWithLeftSidebar>
            </AuthorizedRoute>
          }
        />
      </Route>,
    ),
    { basename: ROUTER_BASEPATH },
  )
  return <RouterProvider router={router} />
}

const HomePageNavigate: React.FC = () => {
  const { onboardingEnabled, gettingStartedEvents } = useGettingStarted()
  const { appendUTMParams } = useUTMHandler()
  return gettingStartedEvents ? (
    <Navigate to={onboardingEnabled ? appendUTMParams('/getting-started', true) : appendUTMParams('/workspace/live-meetings', true)} replace />
  ) : (
    <></>
  )
}
